import { useEffect } from 'react';
import { Form, Formik } from 'formik';
import Button from 'src/components/button/Button';
import ProfilePicture from 'src/components/profile/profilePicture/ProfilePicture';
import InputField from 'src/components/common/formFields/inputField/InputField';
import { componentSizeVariantsEnum } from 'src/constants';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { TEditReplyToCommentRequest, TReply } from 'src/services/apiEndpoint.types.ts';
import { TUserTypes } from 'src/constants/user.constants';

import './editReply.scss';
import editCommentValidationSchema from './editReply.validationSchema';
import { editReplyToComment } from 'src/store/venture/venture.slice';
import { useGetIdentity } from 'src/hooks';

export type TEditReply = {
  onClose: () => void;
  reply: any;
  commentId: string;
  handleGetReplies: () => void;
  viewerId: string;
  viewerType: TUserTypes;
};

export type TEditReplyFormValues = {
  content: string;
};

// This component must be aware of the id and type of the viewer
const EditReply = ({
  reply,
  commentId,
  onClose,
  handleGetReplies,
  viewerId,
  viewerType
}: TEditReply) => {
  const { getIdentities } = useGetIdentity();
  const initialValues: TEditReplyFormValues = { content: reply?.content };

  const replyId = reply?.id;
  const replyEditorProfilePic = viewerType === 'User' ? reply?.User?.profilePic : reply?.Company?.logo;
  const replyEditorName = reply?.User?.name || reply?.Company?.name;
  const { authenticatedId, loggedInUserType } = getIdentities();
  const dispatch = useAppDispatch();

  const editReplyToCommentLoader: boolean = useAppSelector(
    (store) => store.venture.editReplyToCommentLoader
  );

  const handleSubmit = async (
    { content }: TEditReplyFormValues,
    { resetForm }: { resetForm: () => void }
  ) => {
    // edit comment api call
    const payload: TEditReplyToCommentRequest = {
      ...(loggedInUserType === 'Company' ? { companyId: viewerId } : { userId: viewerId }),
      id: replyId,
      // replyId,
      userType: viewerType,
      content,
      commentId
    };

    dispatch(editReplyToComment(payload)).then(() => {
      handleGetReplies();
      resetForm();
      onClose();
    });
  };

  useEffect(() => {
    initialValues.content = reply?.content;
  }, [JSON.stringify(reply)]);

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={editCommentValidationSchema}
        onSubmit={handleSubmit}
      >
        {({ errors, touched, setFieldValue, values }) => {
          return (
            <Form noValidate className="edit-reply">
              <div className="edit-reply__header">
                <ProfilePicture url={replyEditorProfilePic || ''} fallback={replyEditorName?.[0] || ''} />
                <InputField
                  placeholder="Comment"
                  id="content"
                  variant={componentSizeVariantsEnum.SMALL}
                  onChange={(e) => {
                    setFieldValue('content', e.target.value);
                  }}
                  value={values.content}
                  errorMessage={errors.content}
                  isTouched={touched.content}
                />
              </div>
              <div className="edit-reply__buttons">
                <Button variant="secondary" size="small" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="small"
                  type="submit"
                  loading={editReplyToCommentLoader}
                >
                  Reply
                </Button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default EditReply;
